"use client"

import { EyeOffIcon } from "lucide-react"

import { useTranslations } from "@/i18n"
import {
  FinanceDisclosureLevel,
  type FinanceSurfaceContext,
} from "@/features/finance/hooks/finance-context-rules"

/**
 * What a widget shows when its endpoint would REFUSE, rather than what a failure looks like.
 *
 * Some surfaces degrade to shares and some refuse outright (ADR-0024): a fund breakdown is a composition, so
 * a proportion still answers the question, while a transaction list and an investigation are amount-level —
 * a row IS an amount, and a list of percentages of an undisclosed total answers nothing. Those refuse with
 * `BRANCH_FINANCE_NOT_DISCLOSABLE`.
 *
 * The refusal was correct and it rendered as a red "could not load" card with a Retry button, on a board
 * whose own banner said the figures were restricted. Retry cannot help — the server will refuse again, by
 * design — so the button was an invitation to keep hitting a wall.
 *
 * That is the third time the same mistake landed in a new place: withheld shown as `$0.00`, then as removed
 * widgets, now as an error. A governed product has to look governed.
 *
 * The widget decides this from the board's resolved context BEFORE querying, so the request is never made.
 */
export function useIsWithheld(context?: FinanceSurfaceContext): boolean {
  return context?.disclosure === FinanceDisclosureLevel.Shares
}

export function WidgetWithheldState({ context }: { context?: FinanceSurfaceContext }) {
  const t = useTranslations("finance.overview")

  if (!useIsWithheld(context)) return null

  return (
    /*
     * The same faint fill the report lock uses, so the two read as one family rather than two accidents.
     * Nothing failed here; a filled panel says the surface is settled, where bare text on the card
     * background reads as content that did not arrive.
     */
    <div className='border-border/60 bg-muted/30 flex h-full flex-col items-center justify-center gap-2 rounded-lg border px-4 py-6 text-center'>
      <span className='bg-background text-muted-foreground ring-border/60 inline-flex size-8 items-center justify-center rounded-full ring-1'>
        <EyeOffIcon className='size-4' aria-hidden />
      </span>
      <p className='text-muted-foreground text-sm'>{t("widgets.common.withheld.title")}</p>
      <p className='text-muted-foreground/80 text-xs'>{t("widgets.common.withheld.description")}</p>
    </div>
  )
}
